/**
 * MarketContextStrip — Shared market regime/rotation/niche/sector strip.
 *
 * Shows chips for market regime, breadth, sector rotation, niche regime,
 * and top 3 sectors. Can be loaded from `summary.market_context` (inline)
 * or fetched via `marketContextAPI.get()`.
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Chip, Tooltip, alpha,
} from '@mui/material';
import {
  SecurityOutlined, BarChart, SyncAlt, TrackChanges,
  CategoryOutlined, FiberManualRecord,
} from '@mui/icons-material';
import { marketContextAPI } from '../services/api';

const REGIME_CFG = {
  RISK_ON:  { label: 'Risk On',  color: '#10b981' },
  RISK_OFF: { label: 'Risk Off', color: '#ef4444' },
  NEUTRAL:  { label: 'Neutral',  color: '#f59e0b' },
  ALLOW:    { label: 'Allow',    color: '#10b981' },
  WATCH:    { label: 'Watch',    color: '#f59e0b' },
  AVOID:    { label: 'Avoid',    color: '#ef4444' },
};

export default function MarketContextStrip({ summary, standalone = false }) {
  const [fetched, setFetched] = useState(null);

  // If standalone mode (no summary prop), fetch from API
  useEffect(() => {
    if (standalone && !summary) {
      marketContextAPI.get()
        .then(r => setFetched(r.data?.market_context))
        .catch(() => {});
    }
  }, [standalone, summary]);

  const ctx = summary?.market_context || fetched;
  if (!ctx || Object.keys(ctx).length === 0) return null;

  const regime = ctx.regime;
  const rotation = ctx.rotation;
  const niche = ctx.niche;
  const topSectors = ctx.top_sectors;

  const regimeLabel = regime?.label || regime?.alert_level;
  const rcfg = REGIME_CFG[regimeLabel] || REGIME_CFG.NEUTRAL;
  const breadthPct = regime?.breadth_pct_above20;
  const isNiche = niche?.is_niche;

  return (
    <Box sx={{ display: 'flex', gap: 0.8, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
      {regimeLabel && (
        <Chip
          icon={<SecurityOutlined sx={{ fontSize: 13 }} />}
          label={`${rcfg.label}${regime?.risk_score != null ? ` (${regime.risk_score})` : ''}`}
          size="small"
          sx={{
            fontSize: '0.68rem', fontWeight: 700, height: 24,
            bgcolor: alpha(rcfg.color, 0.08), color: rcfg.color,
            border: `1px solid ${alpha(rcfg.color, 0.15)}`,
            '& .MuiChip-icon': { color: rcfg.color },
          }}
        />
      )}

      {breadthPct != null && (
        <Chip
          icon={<BarChart sx={{ fontSize: 13 }} />}
          label={`Breadth ${breadthPct.toFixed(0)}%`}
          size="small"
          sx={{
            fontSize: '0.68rem', fontWeight: 700, height: 24,
            bgcolor: alpha(breadthPct >= 50 ? '#10b981' : breadthPct >= 30 ? '#f59e0b' : '#ef4444', 0.08),
            color: breadthPct >= 50 ? '#059669' : breadthPct >= 30 ? '#d97706' : '#dc2626',
            border: `1px solid ${alpha(breadthPct >= 50 ? '#10b981' : breadthPct >= 30 ? '#f59e0b' : '#ef4444', 0.15)}`,
            '& .MuiChip-icon': { color: breadthPct >= 50 ? '#059669' : breadthPct >= 30 ? '#d97706' : '#dc2626' },
          }}
        />
      )}

      {rotation?.is_rotation && (
        <Chip
          icon={<SyncAlt sx={{ fontSize: 13 }} />}
          label={`Xoay ngành (${rotation.events} sự kiện)`}
          size="small"
          sx={{
            fontSize: '0.68rem', fontWeight: 700, height: 24,
            bgcolor: alpha('#8b5cf6', 0.08), color: '#7c3aed',
            border: `1px solid ${alpha('#8b5cf6', 0.15)}`,
            '& .MuiChip-icon': { color: '#7c3aed' },
          }}
        />
      )}

      {isNiche && (
        <Chip
          icon={<TrackChanges sx={{ fontSize: 13 }} />}
          label={`Niche (${niche.score}/5)`}
          size="small"
          sx={{
            fontSize: '0.68rem', fontWeight: 700, height: 24,
            bgcolor: alpha('#6366f1', 0.08), color: '#4f46e5',
            border: `1px solid ${alpha('#6366f1', 0.15)}`,
            '& .MuiChip-icon': { color: '#4f46e5' },
          }}
        />
      )}

      {topSectors?.length > 0 && (
        <>
          <Box sx={{ width: '1px', height: 16, bgcolor: '#e2e8f0', mx: 0.3 }} />
          <CategoryOutlined sx={{ fontSize: 13, color: '#94a3b8' }} />
          {topSectors.slice(0, 3).map((s) => (
            <Tooltip key={s.code} title={`${s.name} — Cơ hội: ${s.opportunity} / Rủi ro: ${s.risk}`} arrow>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.3,
                px: 0.7, py: 0.15, borderRadius: 1,
                bgcolor: alpha(s.quadrant === 'LEADING' ? '#10b981' : s.quadrant === 'IMPROVING' ? '#3b82f6' : '#94a3b8', 0.06),
                border: `1px solid ${alpha(s.quadrant === 'LEADING' ? '#10b981' : s.quadrant === 'IMPROVING' ? '#3b82f6' : '#94a3b8', 0.12)}`,
              }}>
                <FiberManualRecord sx={{ fontSize: 6, color: s.quadrant === 'LEADING' ? '#10b981' : s.quadrant === 'IMPROVING' ? '#3b82f6' : '#94a3b8' }} />
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#475569' }}>{s.name || s.code}</Typography>
              </Box>
            </Tooltip>
          ))}
        </>
      )}
    </Box>
  );
}
